import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';

/**
 * Validation des conventions de formation par la direction (phase 5,
 * TF-430). Brouillon, à valider, validée, signée : l'état et les gestes
 * ouverts au lecteur viennent de l'API (`domain/validation-conventions.ts`) ;
 * les écrans affichent, ils ne décident rien.
 */

export type EtatConvention = 'BROUILLON' | 'A_VALIDER' | 'VALIDEE' | 'SIGNEE';

export interface EtapeConvention {
  le: string;
  par: string | null;
}

export interface VueConvention {
  etat: EtatConvention;
  libelle: string;
  ton: string;
  importee: boolean;
  soumission: EtapeConvention | null;
  validation: EtapeConvention | null;
  renvoi: (EtapeConvention & { motif: string }) | null;
  refusSoumission: string | null;
  refusValidation: string | null;
  refusRenvoi: string | null;
  refusSignature: string | null;
}

export interface LigneConvention extends VueConvention {
  id: string;
  genereLe: string;
  apprenant: { id: string; nom: string; prenom: string };
  site: { code: string; nom: string };
  mode: 'CFA' | 'OF';
  formation: { code: string; titre: string } | null;
  contrat: { employeur: string; debut: string; fin: string } | null;
  transmission: { le: string; par: string | null; destinataire: string | null } | null;
  attenteJours: number | null;
}

export interface ReponseConventions {
  calculeLe: string;
  compteurs: {
    aValider: number;
    plusAncienneJours: number | null;
    validees: number;
    signees: number;
    brouillons: number;
    renvoyees: number;
  };
  lignes: LigneConvention[];
  options: {
    etats: { valeur: EtatConvention; libelle: string }[];
    formations: { code: string; titre: string }[];
  };
}

export interface ResultatGesteConvention {
  convention: VueConvention;
  message: string;
}

const CLE = ['conventions'];

export function useConventions(site: string) {
  return useQuery({
    queryKey: [...CLE, site],
    queryFn: () => appelApi<ReponseConventions>(`/conventions?site=${encodeURIComponent(site)}`),
    placeholderData: keepPreviousData,
  });
}

function useApresGeste() {
  const client = useQueryClient();
  return (apprenantId: string | undefined) => {
    void client.invalidateQueries({ queryKey: CLE });
    // Le pipeline du tableau de bord compte les conventions à valider et à signer.
    void client.invalidateQueries({ queryKey: ['financements', 'tableau-de-bord-direction'] });
    if (apprenantId) {
      void client.invalidateQueries({ queryKey: ['documents', apprenantId] });
      void client.invalidateQueries({ queryKey: ['dossier', apprenantId] });
    }
  };
}

interface GesteConvention {
  documentId: string;
  apprenantId?: string;
}

export function useSoumettreConvention() {
  const apres = useApresGeste();
  return useMutation({
    mutationFn: ({ documentId }: GesteConvention) =>
      appelApi<ResultatGesteConvention>(`/conventions/${documentId}/soumission`, { methode: 'POST' }),
    onSuccess: (_resultat, { apprenantId }) => apres(apprenantId),
  });
}

export function useValiderConvention() {
  const apres = useApresGeste();
  return useMutation({
    mutationFn: ({ documentId }: GesteConvention) =>
      appelApi<ResultatGesteConvention>(`/conventions/${documentId}/validation`, { methode: 'POST' }),
    onSuccess: (_resultat, { apprenantId }) => apres(apprenantId),
  });
}

export function useRenvoyerConvention() {
  const apres = useApresGeste();
  return useMutation({
    mutationFn: ({ documentId, motif }: GesteConvention & { motif: string }) =>
      appelApi<ResultatGesteConvention>(`/conventions/${documentId}/renvoi`, {
        methode: 'POST',
        corps: { motif },
      }),
    onSuccess: (_resultat, { apprenantId }) => apres(apprenantId),
  });
}
